// Vehicle Compatibility — renders a product's vehicle fitments as a searchable
// table on the product page. Gated on the `showCompatibility` feature flag.
//
// Config (read from the TARGET element, not the script tag):
//   #parts-vehicle-compatibility  OR  [data-parts-vehicle-compatibility]  — container
//   data-sku-selector="#product-sku"  (optional; overrides the SKU source)
//   data-sku="..."                    (optional; explicit SKU on the container)
//
// If no explicit container is present the widget mounts itself as a "Vehicle
// Compatibility" tab in the storefront's Bootstrap product tab strip
// (.nav-tabs-contain), matching the legacy behaviour.

import { getTopDocument } from '../shared/dom.js';
import { esc, getSku } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';
import { loadFeatures, isAnonymous } from '../shared/identity.js';

function _loginUrl() {
  try {
    var origin = (window.top && window.top.location && window.top.location.origin) || window.location.origin;
    var loc = (window.top && window.top.location) || window.location;
    // redirectTo must be a relative PATH (path + query), not the absolute URL.
    return origin + '/account/login?redirectTo=' + encodeURIComponent(loc.pathname + loc.search + loc.hash);
  } catch (e) {
    return '/account/login';
  }
}

// Widget-specific attribute escaper for the data-search filter attribute (the
// shared esc() is for element text, not attribute values).
function escAttr(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Group a part's vehicleSpecificAttributes down to the given carIds → [{name,value}]
// (unique values joined, name-sorted). Mirrors app/src/utils/vehicleSpecificAttrs.ts.
function attrRowsForCarIds(part, carIds) {
  if (!part || !part.vehicleSpecificAttributes || !carIds || carIds.length === 0) return [];
  var active = {};
  for (var i = 0; i < carIds.length; i++) active[carIds[i]] = true;
  var order = [];
  var byName = {};
  part.vehicleSpecificAttributes.forEach(function (a) {
    if (!a || !active[a.carId]) return;
    var name = (a.attributeName || '').trim();
    var value = (a.attributeValue || '').trim();
    if (!name && !value) return;
    if (!byName[name]) { byName[name] = {}; order.push(name); }
    if (value) byName[name][value] = true;
  });
  return order.sort().map(function (name) {
    return { name: name, value: Object.keys(byName[name]).join(', ') };
  });
}

function render(container, sku) {
  container.innerHTML = '<div class="pvc-wrap"><div class="pvc-header"><p class="pvc-title">Vehicle compatibility</p><p class="pvc-subtitle">Fits the following vehicles</p></div><div class="pvc-loading">Loading vehicle compatibility…</div></div>';

  apiJson('/api/parts/' + encodeURIComponent(sku), null, null)
    .then(function (part) {
      var wrap = container.querySelector('.pvc-wrap');
      if (!wrap) return;

      // apiJson resolves to null on any non-OK / network error — clear the
      // widget silently, as the legacy fetch().catch() did.
      if (!part) {
        wrap.innerHTML = '';
        return;
      }

      var fitments = (part && part.fitments) ? part.fitments : [];

      if (fitments.length === 0) {
        wrap.innerHTML = '<div class="pvc-header"><p class="pvc-title">Vehicle compatibility</p><p class="pvc-subtitle">Fits the following vehicles</p></div><div class="pvc-empty">No vehicle compatibility data available for this part.</div>';
        return;
      }

      var html = [
        '<div class="pvc-header">',
        '<p class="pvc-title">Vehicle compatibility</p>',
        '<p class="pvc-subtitle">Fits the following vehicles. Search or filter below</p>',
        '</div>',
        '<div class="pvc-search-wrap">',
        '<span class="pvc-search-icon" aria-hidden="true">',
        '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>',
        '</span>',
        '<input type="text" class="pvc-search" placeholder="Search by year, make, model or engine…" aria-label="Filter vehicles">',
        '<button type="button" class="pvc-clear" aria-label="Clear search" title="Clear">',
        '<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>',
        '</button>',
        '</div>',
        '<p class="pvc-meta pvc-meta-count">Showing ' + fitments.length + ' vehicle' + (fitments.length !== 1 ? 's' : '') + '</p>',
        '<div class="pvc-table-wrap"><div class="pvc-list-scroll">',
        '<table class="pvc-table"><thead><tr>',
        '<th class="pvc-cell-year">Year</th><th>Make</th><th>Model</th><th>Engine / variant</th>',
        '</tr></thead><tbody>'
      ].join('');

      fitments.forEach(function (f, i) {
        var year = (f.fit_year_from && f.fit_year_to) ? f.fit_year_from + ' - ' + f.fit_year_to : (f.fit_year_from || f.fit_year_to || '-');
        var make = f.fit_make || '-';
        var model = (f.fit_model || '') + (f.fit_submodel ? ' ' + f.fit_submodel : '') || '-';
        var engine = f.fit_engine || '-';
        var searchText = [year, f.fit_make, f.fit_model, f.fit_submodel, f.fit_engine].filter(Boolean).join(' ').toLowerCase();
        var attrRows = attrRowsForCarIds(part, f.carIds);
        var hasAttrs = attrRows.length > 0;
        html += '<tr class="pvc-row" data-search="' + escAttr(searchText) + '">';
        html += '<td class="pvc-cell-year">' + esc(year) + '</td>';
        html += '<td class="pvc-cell-make">' + esc(make) + '</td>';
        html += '<td>' + esc(model) + '</td>';
        html += '<td class="pvc-cell-engine"><span class="pvc-engine-text">' + esc(engine) + '</span>';
        if (hasAttrs) {
          // Clickable info icon → reveals this vehicle's vehicle-specific attributes.
          html += '<button type="button" class="pvc-attr-toggle" data-attr-idx="' + i + '" aria-expanded="false" title="Vehicle-specific details" aria-label="Show vehicle-specific details">'
            + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></button>';
        }
        html += '</td></tr>';
        if (hasAttrs) {
          var inner = '';
          for (var r = 0; r < attrRows.length; r++) {
            inner += '<div class="pvc-attr-item"><span class="pvc-attr-name">' + esc(attrRows[r].name) + '</span><span class="pvc-attr-value">' + esc(attrRows[r].value) + '</span></div>';
          }
          html += '<tr class="pvc-attr-row pvc-attr-row-hidden" data-attr-row="' + i + '"><td colspan="4"><div class="pvc-attr-box">' + inner + '</div></td></tr>';
        }
      });
      html += '</tbody></table></div></div>';
      wrap.innerHTML = html;

      var totalCount = fitments.length;
      var searchInput = wrap.querySelector('.pvc-search');
      var clearBtn = wrap.querySelector('.pvc-clear');
      var metaEl = wrap.querySelector('.pvc-meta-count');
      var rows = wrap.querySelectorAll('.pvc-row');
      var tableWrap = wrap.querySelector('.pvc-table-wrap');

      // Toggle a vehicle's specific-attributes expand row (delegated on the tbody).
      var tbody = wrap.querySelector('.pvc-table tbody');
      if (tbody) {
        tbody.addEventListener('click', function (ev) {
          var btn = ev.target && ev.target.closest ? ev.target.closest('.pvc-attr-toggle') : null;
          if (!btn) return;
          var idx = btn.getAttribute('data-attr-idx');
          var attrRow = wrap.querySelector('.pvc-attr-row[data-attr-row="' + idx + '"]');
          if (!attrRow) return;
          var nowHidden = attrRow.classList.toggle('pvc-attr-row-hidden');
          btn.setAttribute('aria-expanded', String(!nowHidden));
          btn.classList.toggle('pvc-attr-toggle-open', !nowHidden);
        });
      }

      function updateFilter() {
        var q = (searchInput.value || '').trim().toLowerCase();
        var visible = 0;
        rows.forEach(function (tr) {
          var show = !q || tr.getAttribute('data-search').indexOf(q) !== -1;
          tr.classList.toggle('pvc-row-hidden', !show);
          // Collapse + hide this row's attribute expand-row when its parent hides.
          var attrRow = tr.nextElementSibling;
          if (attrRow && attrRow.classList && attrRow.classList.contains('pvc-attr-row') && !show) {
            attrRow.classList.add('pvc-attr-row-hidden');
            var tgl = tr.querySelector('.pvc-attr-toggle');
            if (tgl) { tgl.setAttribute('aria-expanded', 'false'); tgl.classList.remove('pvc-attr-toggle-open'); }
          }
          if (show) visible++;
        });
        metaEl.textContent = q
          ? 'Showing ' + visible + ' of ' + totalCount + ' vehicle' + (totalCount !== 1 ? 's' : '')
          : 'Showing ' + totalCount + ' vehicle' + (totalCount !== 1 ? 's' : '');
        clearBtn.classList.toggle('visible', q.length > 0);
        var noMatch = wrap.querySelector('.pvc-no-matches');
        if (visible === 0 && q) {
          if (!noMatch) {
            noMatch = document.createElement('p');
            noMatch.className = 'pvc-no-matches';
            noMatch.textContent = 'No vehicles match your search. Try different words.';
            tableWrap.parentNode.insertBefore(noMatch, tableWrap.nextSibling);
          }
          noMatch.style.display = 'block';
          tableWrap.style.display = 'none';
        } else {
          if (noMatch) noMatch.style.display = 'none';
          tableWrap.style.display = '';
        }
      }
      searchInput.addEventListener('input', updateFilter);
      searchInput.addEventListener('keydown', function (e) { if (e.key === 'Escape') { searchInput.value = ''; updateFilter(); searchInput.focus(); } });
      clearBtn.addEventListener('click', function () { searchInput.value = ''; updateFilter(); searchInput.focus(); });
      updateFilter();
    });
}

// Mount into the storefront's Bootstrap product tab strip when no explicit
// container is on the page. Returns the tab pane, or null if the tab chrome
// isn't present.
function ensureTabContainer() {
  var topDoc = getTopDocument();
  var navTabsContain = topDoc.querySelector('.nav-tabs-contain');
  if (!navTabsContain) return null;
  var tabId = 'pvc-vehiclecompatibility';
  var existing = topDoc.getElementById(tabId);
  if (existing) return existing;
  var tabList = navTabsContain.querySelector('.nav.nav-tabs');
  var tabContent = navTabsContain.querySelector('.tab-content');
  if (!tabList || !tabContent) return null;
  var li = topDoc.createElement('li');
  li.setAttribute('role', 'presentation');
  li.innerHTML = '<a href="#' + tabId + '" role="tab" data-toggle="tab">Vehicle Compatibility</a>';
  tabList.appendChild(li);
  var panel = topDoc.createElement('div');
  panel.setAttribute('role', 'tabpanel');
  panel.className = 'tab-pane';
  panel.id = tabId;
  tabContent.appendChild(panel);
  return panel;
}

export function init() {
  var doc = getTopDocument();

  var container = doc.getElementById('parts-vehicle-compatibility') ||
    doc.querySelector('[data-parts-vehicle-compatibility]');

  // No explicit container? We can still mount into the storefront's product tab
  // strip if it's present. If neither exists, this isn't a compatibility page —
  // no-op cleanly.
  var hasTabHost = !!doc.querySelector('.nav-tabs-contain');
  if (!container && !hasTabHost) return;

  // SKU: explicit data-sku on the container, then data-sku-selector / #product-sku,
  // then any [data-sku] on the page (shared getSku handles the last two).
  var direct = container && (container.getAttribute('data-sku') || '').trim();
  var sku = direct || getSku(container && container.getAttribute('data-sku-selector'));
  if (!sku) {
    console.warn('PartsVehicleCompatibility: No SKU found. Add data-sku on the container, an #product-sku input, or set data-sku-selector.');
    return;
  }

  // Gate the entire widget on `showCompatibility`. Suppress only on an EXPLICIT
  // false so a features outage fails open.
  loadFeatures().then(function (features) {
    if (features && features.showCompatibility === false) {
      console.log('PartsVehicleCompatibility: feature `showCompatibility` is disabled for this role; skipping render.');
      // The template's fallback says "No vehicle compatibility data available", which reads as
      // though the part fits nothing (TP-177 Nov 2026, 12697A). A visitor who isn't signed in is
      // told to sign in instead.
      if (container && isAnonymous()) {
        container.innerHTML = '<div class="pvc-wrap"><div class="pvc-header"><p class="pvc-title">Vehicle compatibility</p></div>' +
          '<div class="pvc-empty"><a href="' + esc(_loginUrl()) + '">Sign in</a> to see the vehicles this part fits.</div></div>';
      }
      return;
    }
    var target = container || ensureTabContainer();
    if (!target) return;
    render(target, sku);
  });
}

export var id = 'vehicle-compatibility';
